import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DeviceMobile, Spinner, SignOut } from "@phosphor-icons/react";
import { fetchSessions, revokeSession } from "@/lib/api";
import { DASHBOARD } from "@/constants/testIds";
import { toast } from "sonner";

const SessionActivityDialog = ({ open, onOpenChange, onCurrentSessionRevoked }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchSessions()
      .then(setSessions)
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  }, [open]);

  const handleRevoke = async (sessionId, isCurrent) => {
    setBusyId(sessionId);
    try {
      await revokeSession(sessionId);
      setSessions((prev) => prev.filter((s) => s.session_id !== sessionId));
      toast.success("Session logged out");
      if (isCurrent) onCurrentSessionRevoked?.();
    } catch {
      toast.error("Could not log out that session");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={DASHBOARD.sessionActivityDialog} className="bg-[#0F0F0F] border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Active Sessions</DialogTitle>
          <DialogDescription className="text-white/50 text-sm">Devices currently logged into your Skilmark account.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-white/40">Loading sessions...</p>
        ) : sessions.length === 0 ? (
          <p className="text-sm text-white/40">No active sessions found.</p>
        ) : (
          <div className="space-y-2">
            {sessions.map((s) => (
              <div key={s.session_id} data-testid={DASHBOARD.sessionActivityItem(s.session_id)} className="flex items-center justify-between gap-3 bg-white/5 rounded-xl px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <DeviceMobile size={18} className="text-white/40 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">
                      {s.device} {s.current && <span className="text-[10px] text-emerald-400 uppercase tracking-wider ml-1">This device</span>}
                    </p>
                    <p className="text-xs text-white/40">
                      {s.method === "google" ? "Google login" : "Email login"} &middot; {new Date(s.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <button
                  data-testid={DASHBOARD.sessionActivityLogoutBtn(s.session_id)}
                  onClick={() => handleRevoke(s.session_id, s.current)}
                  disabled={busyId === s.session_id}
                  className="rounded-full border border-white/15 text-xs px-3 py-1.5 flex items-center gap-1.5 text-red-400/80 hover:border-red-400 hover:text-red-400 transition-colors duration-300 disabled:opacity-40 shrink-0"
                >
                  {busyId === s.session_id ? <Spinner size={12} className="animate-spin" /> : <SignOut size={12} />} Log Out
                </button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SessionActivityDialog;
